import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { path, uniqBy } from "ramda";
import { Form, Formik } from "formik";
import { withTheme } from "styled-components";
import { Box, Card, Flex, Text } from "rebass";

// ------------------------------------------------------------------------

import {
    uk,
    placeHolder,
    ColumnWrapper,
    Warning,
    RightColumn,
    GEO_LOCATION_STATE,
    StyledGrid,
    StyledSelect,
    customStyles,
    MapIcon,
    StyledCard
} from "./styled";

import { GMAPS_URL } from "constants/";
import MarkerClusterer from "@google/markerclusterer";
import { MediaQuery } from "@sisuwellness/web-components";
import { findClosestMarker } from "../../utilities/mapFunction/index";
import InputField from "components/InputField";
import getGoogleSuggestionsSetup from "utilities/googlePlacesSuggestions";
import { useTranslation } from "react-i18next";

const defaultContent = t => {
    return {
        logo: null,
        title: t("members_portal_web.views.find_a_station.find"),
        description: t("members_portal_web.views.find_a_station.tool_text"),
        mapMarkerUrl: "/images/google-maps-pin-sh.svg",
        mapClusterUrl: "/images/google-maps-cluster.png"
    };
};

// ------------------------------------------------------------------------

const FindAStation = ({ installations, region, theme, content, detectLocation = true }) => {
    const { t } = useTranslation();
    content = content || defaultContent(t);
    const [visibleInstallations, setVisibleInstallations] = useState([]);
    const [selectedInstallation, setSelectedInstallation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(region);
    const [markers, setMarkers] = useState({});
    const [startNode, setStartNode] = useState();
    const [google, setGoogle] = useState(window.google);
    const [selectedCard, setSelectedCard] = useState(null);

    const map = useRef();
    const markerClusterer = useRef();
    const initialLocation = useRef();
    const mapRef = useRef();
    const inputRef = useRef();
    const directionsService = useRef();
    const directionsRenderer = useRef();
    const searchedLocation = useRef();
    const autoCompleteService = useRef(null);
    const placesService = useRef(null);
    const autoCompleteSessionToken = useRef(null);

    const suggestionsSetup = useRef(
        getGoogleSuggestionsSetup(autoCompleteService, placesService, autoCompleteSessionToken)
    );

    const getPostalCode = suggestionsSetup.current.getPostalCode;

    const handleOnSuggestionsFetch = (searchValue, setResultsCallback) =>
        suggestionsSetup.current.handleOnSuggestionsFetch(searchValue, setResultsCallback, selectedCountry);

    const handleGetSuggestionValue = suggestionsSetup.current.handleGetSuggestionValue;

    const handleRenderSuggestion = suggestionsSetup.current.handleRenderSuggestion;

    /**
     * Attempts to determine and store the user's current location
     * On failure, store the failure reason to communicate back to the user
     */
    const getCurrentPosition = () => {
        /**
         * Sets the initial position with given lat/long, and triggers a map center change event
         * - falls back to a region-specific default (SiSU UK HQ)
         *
         * @param {Number|null} latitude
         * @param {Number|null} longitude
         */
        const setInitialLocation = (latitude = null, longitude = null) => {
            // Check if Google Maps API is fully loaded and LatLng constructor is available
            if (!google || !google.maps || typeof google.maps.LatLng !== "function") {
                // Try again after a delay
                setTimeout(() => setInitialLocation(latitude, longitude), 500);
                return;
            }

            if (latitude == null || longitude == null) {
                const region = selectedCountry || "GB";
                switch (region) {
                    default:
                        latitude = 51.5291596;
                        longitude = -0.080698;
                        break;
                }
            }

            try {
                initialLocation.current = new google.maps.LatLng(latitude, longitude);
                onMapCenterChanged();
            } catch (error) {
                // If there's an error creating the LatLng, wait and try again
                setTimeout(() => setInitialLocation(latitude, longitude), 1000);
            }
        };

        // Determine initial location
        if (detectLocation && "geolocation" in navigator) {
            const success = position => {
                setInitialLocation(position.coords.latitude, position.coords.longitude);
                setLocationError(null);
            };

            const error = error => {
                let locationError = null;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        locationError = GEO_LOCATION_STATE.DENIED;
                        break;

                    default:
                        locationError = GEO_LOCATION_STATE.UNKNOWN;
                        break;
                }

                setInitialLocation();
                setLocationError(locationError);
            };

            navigator.geolocation.getCurrentPosition(success, error);
        } else {
            // no geolocation available, use default position
            setInitialLocation();
        }
    };

    // Function to load Google Maps API and return a promise
    const loadGoogleMapsApi = () => {
        return new Promise((resolve, reject) => {
            // Check if Google Maps API is already loaded
            if (window.google && window.google.maps && window.google.maps.Geocoder) {
                resolve(window.google);
                return;
            }

            // Check if script is already in the DOM
            if (document.querySelector("#google-maps-api")) {
                // Script is already loading, wait for it to complete
                const checkGoogleInterval = setInterval(() => {
                    if (window.google && window.google.maps && window.google.maps.Geocoder) {
                        clearInterval(checkGoogleInterval);
                        resolve(window.google);
                    }
                }, 100);
                return;
            }

            // Create and append the script
            const googleMapsScript = document.createElement("script");
            googleMapsScript.id = "google-maps-api";
            googleMapsScript.src = GMAPS_URL;

            // Set up the callback for when the script loads
            googleMapsScript.onload = () => {
                // Check if the API is fully loaded with required components
                const checkApiLoaded = setInterval(() => {
                    if (window.google && window.google.maps && window.google.maps.Geocoder) {
                        clearInterval(checkApiLoaded);
                        resolve(window.google);
                    }
                }, 100);
            };

            // Handle script load error
            googleMapsScript.onerror = () => {
                reject(new Error("Google Maps API failed to load"));
            };

            // Add the script to the DOM
            document.head.appendChild(googleMapsScript);
        });
    };

    useEffect(() => {
        // Load Google Maps API and initialize the map
        loadGoogleMapsApi()
            .then(googleApi => {
                setGoogle(googleApi);
                if (mapRef.current) {
                    initialiseMapsCallback();
                }
            })
            .catch(() => {
                // Failed to load Google Maps API
                console.error("Failed to load Gmaps API");
            });
    }, []);

    useEffect(() => {
        setSelectedCountry(region);
    }, [region]);

    /**
     * Handler for location search submission
     * @param event
     */
    const handleSubmit = event => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps) {
            return;
        }

        const searchTerm = event.postCode || "";
        const region = selectedCountry;

        try {
            // Create a new Geocoder instance
            const geocoder = new google.maps.Geocoder();

            // Geocode the address
            geocoder.geocode({ address: searchTerm, region }, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
                    // Store the geocoded location
                    searchedLocation.current = results[0];

                    // Find the closest marker to the geocoded location
                    const data = findClosestMarker(
                        installations,
                        searchedLocation.current.geometry.location.lat(),
                        searchedLocation.current.geometry.location.lng()
                    );

                    // Fit the map bounds to include both the searched location and the closest marker
                    fitBound(data);

                    // Draw a path between the searched location and the closest marker
                    pathBetween(data);
                }
            });
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => handleSubmit(event), 500);
        }
    };

    const getUniqueAddresses = () => {
        return installations.every(installation => path(["address", "formattedAddress"], installation))
            ? uniqBy(installation => installation.address.formattedAddress, installations)
            : installations;
    };

    const fitBound = data => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !map.current) {
            return;
        }

        try {
            // Create a new bounds object
            const bounds = new google.maps.LatLngBounds();

            // Add the start and destination points to the bounds
            bounds.extend(new google.maps.LatLng(parseFloat(data.start.lat), parseFloat(data.start.lng)));
            bounds.extend(new google.maps.LatLng(parseFloat(data.dest.lat), parseFloat(data.dest.lng)));

            // Fit the map to the bounds
            map.current.fitBounds(bounds);

            // Remove the existing start marker if it exists
            if (startNode && startNode.setMap) {
                startNode.setMap(null);
            }

            // Create a new marker for the start point
            setStartNode(
                new google.maps.Marker({
                    position: new google.maps.LatLng(parseFloat(data.start.lat), parseFloat(data.start.lng)),
                    label: "A",
                    map: map.current
                })
            );
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => fitBound(data), 500);
        }
    };

    const pathBetween = data => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !map.current) {
            return;
        }

        try {
            // Create LatLng objects for the origin and destination
            const origin = new google.maps.LatLng(parseFloat(data.start.lat), parseFloat(data.start.lng));
            const destination = new google.maps.LatLng(parseFloat(data.dest.lat), parseFloat(data.dest.lng));

            // Set up the directions renderer
            directionsRenderer.current.setMap(map.current);
            directionsRenderer.current.setOptions({ suppressMarkers: true });

            // Create a new directions service
            directionsService.current = new google.maps.DirectionsService();

            // Request directions
            directionsService.current.route(
                {
                    origin: origin,
                    destination: destination,
                    travelMode: google.maps.TravelMode.DRIVING
                },
                (response, status) => {
                    if (status === "OK") {
                        directionsRenderer.current.setDirections(response);
                    }
                }
            );
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => pathBetween(data), 500);
        }
    };

    const initialiseMapsCallback = () => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !mapRef.current) {
            return;
        }

        try {
            // Create the map instance
            map.current = new google.maps.Map(mapRef.current, { zoom: 16 });

            // Create the directions renderer
            directionsRenderer.current = new google.maps.DirectionsRenderer();

            // If a region is specified, locate to that country
            if (region != null) {
                locateToCountry(region);
            }

            // Initialize the map with markers and other elements
            initialiseMap(map.current);
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(initialiseMapsCallback, 500);
        }
    };
    const locateToCountry = code => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !map.current) {
            return;
        }

        const placeId = getPlaceId(code);
        try {
            // Create a new Geocoder instance
            const geocoder = new google.maps.Geocoder();

            // Geocode the place ID
            geocoder.geocode({ placeId }, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
                    // Center the map on the geocoded location
                    map.current.setCenter(results[0].geometry.location);
                    map.current.fitBounds(results[0].geometry.viewport);
                }
            });
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => locateToCountry(code), 500);
        }
    };

    /**
     * Callback function for window.google.Maps API
     * Called upon map initialisation
     */
    const initialiseMap = maps => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !maps) {
            return;
        }

        try {
            // Create a bounds object to fit all markers
            const bounds = new google.maps.LatLngBounds();

            // Create an info window for marker clicks
            const infoWindow = new google.maps.InfoWindow({
                maxWidth: 400
            });

            // Set up marker clusterer options
            const clusterOptions = {
                styles: [
                    {
                        height: 60,
                        url: window.PUBLIC_URL + content.mapClusterUrl,
                        width: 60,
                        textSize: 20
                    }
                ]
            };

            // Create a new marker clusterer
            markerClusterer.current = new MarkerClusterer(maps, [], clusterOptions);

            // Object to store markers by installation ID
            let tempObj = {};

            // Create markers for each installation
            for (let installation of getUniqueAddresses()) {
                // Get the position of the installation
                const position = {
                    lat: installation.address.latitude,
                    lng: installation.address.longitude
                };

                // Create a directions URL for the installation
                const directionsUrl =
                    "https://www.google.com/maps/dir/?api=1&destination=" +
                    encodeURI(installation.address.formattedAddress);

                // Create the content for the info window
                let markerContent = '<div style="padding: 10px;">';
                markerContent =
                    '<h2 style="font-family: Proxima Nova Regular, Arial, sans-serif; margin-top: 0;">' +
                    installation.name +
                    "</h2>";
                markerContent +=
                    '<h4 style="font-family: Proxima Nova Regular, Arial, sans-serif;">' +
                    installation.address.formattedAddress +
                    "</h4>";
                markerContent +=
                    '<div style="font-family: Proxima Nova Regular, Arial, sans-serif;"><a href="' +
                    directionsUrl +
                    '" target="_blank">Click here for Directions</a></div>';
                markerContent += "</div>";

                // Create a marker for the installation
                const marker = new google.maps.Marker({
                    position: position,
                    icon: {
                        url: window.PUBLIC_URL + content.mapMarkerUrl,
                        scaledSize: new google.maps.Size(60, 60)
                    },
                    map: map.current
                });

                // Add the marker position to the bounds
                bounds.extend(marker.position);

                // Add a click listener to the marker
                marker.addListener("click", () => {
                    setSelectedInstallation(installation.healthStationInstallationId);
                    infoWindow.setContent(markerContent);
                    maps.setZoom(14);
                    maps.panTo(marker.position);
                    infoWindow.open(maps, marker);
                });

                // Add the marker to the clusterer
                markerClusterer.current.addMarker(marker);

                // Store the marker by installation ID
                tempObj[installation.healthStationInstallationId] = marker;
            }

            // Store the markers in state
            setMarkers(tempObj);

            // Fit the map to the bounds
            maps.fitBounds(bounds);

            // Add listeners for map events
            maps.addListener("bounds_changed", onMapCenterChanged);
            maps.addListener("center_changed", onMapCenterChanged);

            // Get the user's current position
            getCurrentPosition();
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => initialiseMap(maps), 500);
        }
    };
    /**
     * Handler for clicking an installation marker
     * @param stationInstallationId
     */
    const onClickInstallationMarker = stationInstallationId => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !google.maps.event) {
            return;
        }

        try {
            // Get the marker for the installation
            const marker = markers[stationInstallationId];

            // Set the selected card
            setSelectedCard(stationInstallationId);

            // Trigger the click event on the marker
            google.maps.event.trigger(marker, "click");
        } catch (error) {
            // If there's an error, try again after a short delay
            setTimeout(() => onClickInstallationMarker(stationInstallationId), 500);
        }
    };

    const onMapCenterChanged = () => {
        // At this point, we should have a fully loaded Google Maps API
        // But let's double-check to be safe
        if (!google || !google.maps || !map.current || !map.current.getBounds()) {
            return;
        }

        try {
            // Check if geometry library is available
            const hasGeometryLibrary = google.maps.geometry && google.maps.geometry.spherical;

            // Get the current bounds of the map
            const bounds = map.current.getBounds();

            // Array to store visible installations
            const visibleInstallations = [];

            // Loop through all installations
            for (let installation of getUniqueAddresses()) {
                // Create a LatLng object for the installation position
                const position = new google.maps.LatLng(installation.address.latitude, installation.address.longitude);

                // Check if the installation is within the current map bounds
                if (bounds.contains(position)) {
                    // Create an object to represent the visible installation
                    const visibleInstallation = {
                        id: installation.healthStationInstallationId,
                        name: installation.name,
                        address: installation.address.formattedAddress,
                        distance: 0
                    };

                    // Only calculate distances if geometry library is available
                    if (hasGeometryLibrary) {
                        if (initialLocation.current && !searchedLocation.current) {
                            // Calculate distance from initial location
                            const latLong = new google.maps.LatLng(
                                installation.address.latitude,
                                installation.address.longitude
                            );
                            const distance = google.maps.geometry.spherical.computeDistanceBetween(
                                latLong,
                                initialLocation.current
                            );
                            visibleInstallation.distance = distance;
                            visibleInstallation.distanceString = Number(distance / 1000).toFixed(2) + "km";
                        } else if (searchedLocation.current) {
                            // Calculate distance from searched location
                            const latLongDest = new google.maps.LatLng(
                                installation.address.latitude,
                                installation.address.longitude
                            );
                            const latLongStart = new google.maps.LatLng(
                                searchedLocation.current.geometry.location.lat(),
                                searchedLocation.current.geometry.location.lng()
                            );
                            const distance = google.maps.geometry.spherical.computeDistanceBetween(
                                latLongStart,
                                latLongDest
                            );
                            visibleInstallation.distance = distance;
                            visibleInstallation.distanceString = Number(distance / 1000).toFixed(2) + "km";
                        }
                    } else {
                        // If geometry library is not available, just show without distance
                        visibleInstallation.distanceString = "";
                    }

                    // Add the installation to the visible installations array
                    visibleInstallations.push(visibleInstallation);
                }
            }
            // Sort the visible installations by distance
            // insertion sort based on distance
            for (let i = 0, j = 0; i < visibleInstallations.length; i++) {
                let installation = visibleInstallations[i];

                for (j = i - 1; j > -1 && visibleInstallations[j].distance > installation.distance; j--) {
                    visibleInstallations[j + 1] = visibleInstallations[j];
                }

                visibleInstallations[j + 1] = installation;
            }

            // Update the state with the sorted visible installations
            setVisibleInstallations(visibleInstallations);
        } catch (error) {
            // If there's an error, return without updating the visible installations
            return;
        }
    };

    /**
     * Generates (and pads up to) the closest 3 results from all visible installations
     * @returns {Array}
     */
    const renderResults = () => {
        const results = [];
        for (let installation of visibleInstallations) {
            const classes = selectedInstallation === installation.id ? "selected" : "";
            const result = (
                <StyledCard
                    className={classes}
                    key={`installation-${installation.id}`}
                    bg={["#ebe9f0", "white", "white"]}
                    active={installation.id === selectedCard}
                    onClick={() => onClickInstallationMarker(installation.id)}
                >
                    <Text
                        data-testid={`result-header-${installation.id}`}
                        fontSize="15px"
                        fontWeight="500"
                        color={theme.colours.blackTints700.hex}
                    >
                        {installation.name}
                    </Text>
                    <Text
                        data-testid={`result-address-${installation.id}`}
                        fontSize="11px"
                        fontWeight="normal"
                        color={theme.colours.blackTints700.hex}
                        opacity="0.5"
                        my="10px"
                    >
                        {installation.address}
                    </Text>
                    <Text
                        data-testid={`result-distance-${installation.id}`}
                        fontSize="11px"
                        fontWeight="normal"
                        color={theme.colours.blackTints700.hex}
                        opacity="0.5"
                    >
                        {installation.distanceString}
                    </Text>
                </StyledCard>
            );
            results.push(result);
            if (results.length === 2) {
                break;
            }
        }

        // pad the results list to ensure 2 results
        while (results.length < 2) {
            results.push(<Card key={"dummy-installation-" + (results.length - 1)} />);
        }

        return <Card mt={["24px", "0px"]}>{results}</Card>;
    };

    const renderWarning = () => {
        let warning = [];
        if (locationError) {
            warning.push(
                <p key={"unable-to-determine-location"}>
                    {t("members_portal_web.views.find_a_station.location_error.not_found")}
                </p>
            );

            // unknown geolocation state, offer retry
            if (locationError === GEO_LOCATION_STATE.UNKNOWN) {
                warning.push(
                    <p key={"try-enable-location"}>
                        <a onClick={getCurrentPosition}>
                            {t("members_portal_web.views.find_a_station.location_error.retry.button_label")}
                        </a>{" "}
                        {t("members_portal_web.views.find_a_station.location_error.retry.text")}
                    </p>
                );
                // denied geolocation state, suggest re-enable
            } else if (locationError === GEO_LOCATION_STATE.DENIED) {
                warning.push(
                    <p key={"please-check-location-service"}>
                        {t("members_portal_web.views.find_a_station.location_error.browser_text")}
                    </p>
                );
            }
        }

        return warning.length > 0 ? <Warning>{warning}</Warning> : null;
    };

    const getPlaceId = alpha2 => {
        switch (alpha2) {
            case "GB":
            default:
                return "ChIJqZHHQhE7WgIReiWIMkOg-MQ";
        }
    };

    useEffect(() => {
        map.current && selectedCountry && locateToCountry(selectedCountry);
    }, [selectedCountry]);

    const handleOnChange = e => {
        setSelectedCountry(e.value);
    };

    const availableStationCountries = [{ label: uk, value: "GB" }];
    return (
        <Card py={["0", "24px"]} bg="white" width="100%">
            <Text
                fontSize={["28px", "34px"]}
                color={theme.colours.hpPrimaryPurple.hex}
                fontWeight={theme.fontWeight.intermediate}
            >
                {t("members_portal_web.views.find_a_station.find")}
            </Text>
            <Text mt="16px" mb="32px" color={theme.colours.hpPrimaryPurple.hex} fontSize="15px" fontWeight="normal">
                {t("members_portal_web.views.find_a_station.tool_text")}
            </Text>
            {renderWarning()}
            <ColumnWrapper flexDirection={["column", "row"]}>
                <Flex
                    flexDirection="column"
                    mr={["0px", "40px"]}
                    width={["100%", "auto"]}
                    bg={theme.colours.hpPurple100.hex}
                    flex={["normal", "2 0", "1 0"]}
                    style={{ borderRadius: "10px" }}
                >
                    <MediaQuery devices={["tablet", "desktop"]}>
                        <Text
                            fontSize="17px"
                            fontWeight="500"
                            color={theme.colours.blackTints700.hex}
                            my="16px"
                            px={["13px", "24px"]}
                        >
                            {t("members_portal_web.views.find_a_station.find")}
                        </Text>
                        <Box
                            as="hr"
                            bg="#4d4d4d"
                            mb="16px"
                            css={{
                                border: 0,
                                height: "1px",
                                opacity: "0.1"
                            }}
                        />
                    </MediaQuery>
                    <Box width={"100%"} px={["13px", "24px"]} mt={["16px", "0"]}>
                        <StyledGrid flexDirection="column">
                            {!region && (
                                <StyledSelect
                                    name={"countryCode"}
                                    inputId={"countryCode"}
                                    isSearchable={true}
                                    options={availableStationCountries}
                                    placeholder={placeHolder}
                                    onChange={e => handleOnChange(e)}
                                    defaultValue={"GB"}
                                    styles={customStyles}
                                />
                            )}
                            <Formik initialValues={{ postCode: "" }} onSubmit={handleSubmit}>
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleBlur,
                                    handleSubmit,
                                    handleChange,
                                    setFieldValue
                                }) => {
                                    const Map = <MapIcon height="100%" stroke="grey" />;
                                    const handleOnInputChange = ({ newValue, method }) => {
                                        if (["click", "up", "down", "enter"].includes(method))
                                            handleValuesOnChangeMethod(newValue, method);
                                    };
                                    const handleValuesOnChangeMethod = (newValue, method) => {
                                        if (typeof newValue === "string") {
                                            return;
                                        }
                                        const arr = newValue.terms;
                                        let suburb = "",
                                            street = "",
                                            state = "";
                                        if (arr.length === 3) {
                                            street = arr[0].value;
                                            suburb = arr[0].value;
                                            state = arr[1].value;
                                        } else if (arr.length === 4) {
                                            street = arr[0].value;
                                            suburb = arr[1].value;
                                            state = arr[2].value;
                                        } else if (arr.length > 4) {
                                            state = arr[arr.length - 2].value;
                                            suburb = arr[arr.length - 3].value;
                                            street = arr
                                                .slice(0, arr.length - 3)
                                                .map(item => item.value)
                                                .join(", ");
                                        }

                                        getPostalCode(google, newValue.place_id, postalCode => {
                                            populateAddressFields(state, suburb, street, postalCode, method);
                                        });
                                    };

                                    const populateAddressFields = (state, suburb, street, postCode, method) => {
                                        const value = street + " " + suburb + " " + state + ", " + postCode;
                                        setFieldValue("postCode", value);
                                        if (method === "click") handleSubmit();
                                    };

                                    return (
                                        <Form onSubmit={handleSubmit} data-testid="search-form">
                                            <Box mb="16px">
                                                <InputField
                                                    className={"postcode"}
                                                    leftChild={Map}
                                                    customStyles={`
                                                        background: ${
                                                            selectedCountry ? "#ffffff" : theme.colours.hpGrey200.hex
                                                        };
                                                        border: none;
                                                    `}
                                                    inputProps={{
                                                        name: "postCode",
                                                        placeholder: t(
                                                            "members_portal_web.views.find_a_station.placeholder"
                                                        ),
                                                        "data-testid": "postCode",
                                                        onChange: (event, options) => {
                                                            handleChange(event);
                                                            handleOnInputChange(options);
                                                        },
                                                        value: values.postCode,
                                                        onBlur: handleBlur,
                                                        ref: inputRef,
                                                        id: "text-field-id-postcode",
                                                        disabled: selectedCountry == null
                                                    }}
                                                    fontFamily="GT Walsheim Pro"
                                                    autoSuggest={true}
                                                    handleOnSuggestionsFetch={handleOnSuggestionsFetch}
                                                    handleGetSuggestionValue={handleGetSuggestionValue}
                                                    handleRenderSuggestion={handleRenderSuggestion}
                                                    error={touched.postCode ? errors.postCode : ""}
                                                />
                                            </Box>
                                            <MediaQuery devices={["tablet", "desktop"]}>{renderResults()}</MediaQuery>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </StyledGrid>
                    </Box>
                </Flex>
                <RightColumn p="16px" bg={theme.colours.hpPurple100.hex}>
                    <Box
                        data-testid="google-map-container"
                        style={{ width: "100%", height: "100%", borderRadius: "10px" }}
                        ref={mapRef}
                    />
                </RightColumn>
            </ColumnWrapper>
            <MediaQuery devices={["mobile"]}>
                <Box mb="30px">{renderResults()}</Box>
            </MediaQuery>
        </Card>
    );
};

FindAStation.propTypes = {
    installations: PropTypes.arrayOf(PropTypes.object),
    theme: PropTypes.object,
    region: PropTypes.string,
    content: PropTypes.object,
    detectLocation: PropTypes.bool
};

export default withTheme(FindAStation);
